import type { CharacterMemory } from './memoryTypes';
import type { CharacterOperation } from './operationTypes';

export interface CharacterOperationInput {
  strategy?: unknown;
  memory: CharacterMemory;
  today_index: number;
}

const THRESHOLDS = {
  fatigue: 0.7,
  commercial: 0.6,
  interaction: 0.25,
  energy: 0.7,
  trust: 0.5,
} as const;

function ruleHighFatigue(): CharacterOperation {
  return {
    today_focus: 'Recovery',
    today_goal: 'Rest',
    today_energy: 'low',
    today_social_mode: 'offline',
    today_business_mode: 'none',
    today_content_mode: 'rest',
    today_workload: 'none',
    today_rest_level: 'high',
    today_emotion: 'resting',
    today_visibility: 'hidden',
    today_priority: 100,
    today_reason: ['角色近期工作量偏高，今天优先恢复状态。'],
  };
}

function ruleHighCommercialRatio(): CharacterOperation {
  return {
    today_focus: 'Life',
    today_goal: 'BuildTrust',
    today_energy: 'medium',
    today_social_mode: 'normal',
    today_business_mode: 'none',
    today_content_mode: 'life',
    today_workload: 'low',
    today_rest_level: 'light',
    today_emotion: 'calm',
    today_visibility: 'normal',
    today_priority: 90,
    today_reason: ['最近商业内容偏多，今天回到生活感与信任建立。'],
  };
}

function ruleLowInteraction(): CharacterOperation {
  return {
    today_focus: 'Conversation',
    today_goal: 'IncreaseInteraction',
    today_energy: 'medium',
    today_social_mode: 'active',
    today_business_mode: 'none',
    today_content_mode: 'interaction',
    today_workload: 'low',
    today_rest_level: 'none',
    today_emotion: 'calm',
    today_visibility: 'boost',
    today_priority: 80,
    today_reason: ['近期互动偏低，今天优先安排轻互动与回应。'],
  };
}

function ruleHighEnergyHighTrust(): CharacterOperation {
  return {
    today_focus: 'Business',
    today_goal: 'LightPromotion',
    today_energy: 'high',
    today_social_mode: 'active',
    today_business_mode: 'light',
    today_content_mode: 'promotion',
    today_workload: 'medium',
    today_rest_level: 'none',
    today_emotion: 'excited',
    today_visibility: 'normal',
    today_priority: 70,
    today_reason: ['角色状态与信任基础良好，今天可安排轻量自然推广。'],
  };
}

function ruleBaseFallback(): CharacterOperation {
  return {
    today_focus: 'Life',
    today_goal: 'BuildTrust',
    today_energy: 'medium',
    today_social_mode: 'normal',
    today_business_mode: 'none',
    today_content_mode: 'life',
    today_workload: 'low',
    today_rest_level: 'none',
    today_emotion: 'calm',
    today_visibility: 'normal',
    today_priority: 50,
    today_reason: ['使用基础角色营运节奏。'],
  };
}

export function evaluateOperationRules(memory: CharacterMemory): CharacterOperation {
  if (memory.fatigue_score >= THRESHOLDS.fatigue) {
    return ruleHighFatigue();
  }

  if (memory.commercial_ratio >= THRESHOLDS.commercial) {
    return ruleHighCommercialRatio();
  }

  if (memory.interaction_ratio <= THRESHOLDS.interaction) {
    return ruleLowInteraction();
  }

  if (memory.energy_level >= THRESHOLDS.energy && memory.trust_ratio >= THRESHOLDS.trust) {
    return ruleHighEnergyHighTrust();
  }

  return ruleBaseFallback();
}
